"use client";

import { useEffect } from "react";
import Script from "next/script";

/* The Anjouan licence verification seal, as the whitelabel embeds it: the
   authority's own script from `{id}.snippet.anjouangaming.org` injects a
   clickable seal into the target div, and validates the licence when a
   visitor clicks it. Only the official embed counts — a static shield image
   asserts nothing.

   The id is operator configuration and becomes part of a script hostname, so
   it is validated to the seal id's own shape before anything loads — a value
   that could steer the subdomain anywhere else renders nothing, the same
   stance the Smartico loader takes. */

const SEAL_ID = /^[a-zA-Z0-9-]{8,64}$/;

export const isAnjouanSealId = (value: string): boolean => SEAL_ID.test(value);

/* The script's `init` writes the seal from `document.location.host`: a
   validate link carrying `?domain=<host>` and a seal image whose URL bakes the
   same host in, which the authority renders as valid only for the domain the
   licence names. On a demo that host is a subdomain and the seal comes up
   invalid for a site that is licensed. The layout stamps the apex the licence
   actually covers on `<html data-licensed-host>` — the same apex the sign-in
   Origin presents — and this swaps it in wherever the script wrote the
   browser's host. With no stamp (a preview, localhost) the script's own
   output stands, since those hosts hold no licence to claim. */
const rebindSealHost = (container: HTMLElement) => {
  const licensed = document.documentElement.dataset.licensedHost;
  const own = window.location.host;

  if (!licensed || licensed === own) {
    return;
  }

  for (const element of container.querySelectorAll("a[href], img[src]")) {
    const attribute = element.tagName === "A" ? "href" : "src";
    const value = element.getAttribute(attribute);

    if (value?.includes(own)) {
      element.setAttribute(attribute, value.replaceAll(own, licensed));
    }
  }
};

/* Init writes the seal into the placeholder, then the host rebind above
   fixes its links. One helper, called both from the script's onLoad (first
   page load) and from every mount (client-side navigation, where the script
   is already loaded and onLoad never fires again). */
const initSeal = (licenseId: string) => {
  const w = window as unknown as Record<string, { init?: () => void } | undefined>;
  w[`anj_${licenseId.replaceAll("-", "_")}`]?.init?.();

  const container = document.getElementById(`anj-${licenseId}`);
  if (container) {
    rebindSealHost(container);
  }
};

export function AnjouanSeal({ licenseId }: { licenseId: string }) {
  /* next/script loads a given src once per page session and fires onLoad only
     on that first load. When this component remounts on a client-side
     navigation the placeholder div is fresh and empty but the script is
     already loaded, so nothing re-injects the seal — it stays blank until a
     full refresh. Re-running init on every mount covers that case; on the
     first load the global does not exist yet and onLoad handles it. */
  useEffect(() => {
    if (isAnjouanSealId(licenseId)) {
      initSeal(licenseId);
    }
  }, [licenseId]);

  if (!isAnjouanSealId(licenseId)) {
    return null;
  }

  return (
    <>
      <Script
        src={`https://${licenseId}.snippet.anjouangaming.org/anj-seal.js`}
        id="anjouan-gaming-seal-script"
        data-anj-seal-id={licenseId}
        strategy="afterInteractive"
        onLoad={() => initSeal(licenseId)}
      />
      <div
        id={`anj-${licenseId}`}
        data-anj-seal-id={licenseId}
        data-anj-image-size="35"
        data-anj-image-type="basic-small"
        aria-label="Anjouan gaming licence seal"
      />
    </>
  );
}
